import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useBusinesses } from "@/hooks/use-businesses";
import { useToast } from "@/hooks/use-toast";

export function useChartOfAccounts() {
  const { activeBusiness } = useBusinesses();
  const { toast } = useToast();
  const businessId = activeBusiness?.id;

  // Get account templates (business types)
  const {
    data: accountTemplates = [],
    isLoading: isLoadingTemplates,
    error: templatesError,
  } = useQuery<any[]>({
    queryKey: ["/api/account-templates"],
    // Always fetch templates regardless of business selection
  });

  // Get chart of accounts for the current business
  const {
    data: chartOfAccounts,
    isLoading: isLoadingChart,
    error: chartError,
    refetch: refetchChart,
  } = useQuery<any>({
    queryKey: ["/api/businesses", businessId, "chart-of-accounts"],
    enabled: !!businessId,
  });
  
  console.log("Chart query:", ["/api/businesses", businessId, "chart-of-accounts"]);
  
  // If we have a business but not a chart of accounts, it's returning the business instead
  const hasValidChart = chartOfAccounts && (chartOfAccounts.id !== businessId);

  // Get accounts for the chart of accounts
  const {
    data: accounts = [],
    isLoading: isLoadingAccounts,
    error: accountsError,
    refetch: refetchAccounts,
  } = useQuery<any[]>({
    queryKey: ["/api/chart-of-accounts", hasValidChart ? chartOfAccounts?.id : null, "accounts"],
    enabled: !!chartOfAccounts?.id && hasValidChart,
  });

  // Create chart of accounts from template
  const generateChartMutation = useMutation({
    mutationFn: async (params: string | { businessType: string, forceLlcAccounts?: boolean, regenerate?: boolean }) => {
      if (!businessId) {
        throw new Error("No active business selected");
      }
      
      // Extract business type from params
      const businessTypeString = typeof params === 'object' ? params.businessType : params;
      const forceLlcAccounts = typeof params === 'object' ? params.forceLlcAccounts : false;
      const forceRegenerate = typeof params === 'object' ? params.regenerate : false;
      
      console.log("Generating chart of accounts:", 
                  "Selected type:", businessTypeString, 
                  "Force LLC:", forceLlcAccounts,
                  "Force Regenerate:", forceRegenerate,
                  "Is LLC:", activeBusiness?.name?.includes('LLC'),
                  "Business name:", activeBusiness?.name);
      
      try {
        // First check if we have an existing chart of accounts
        if (!forceRegenerate) {
          const checkResponse = await fetch(`/api/businesses/${businessId}/chart-of-accounts`);
          const existingChart = checkResponse.ok ? await checkResponse.json() : null;
          
          if (existingChart && existingChart.id) {
            try {
              // Import our custom confirmation dialog
              const { showConfirmDialog } = await import('@/hooks/use-confirm-dialog');
              
              // Show a beautiful warning dialog with icon
              const confirmed = await showConfirmDialog({
                title: 'Warning',
                message: `This business already has a chart of accounts set up. Applying a new ${businessTypeString.replace(/_/g, ' ')} industry template will overwrite the existing accounts. Do you want to continue?`,
                confirmText: 'Continue',
                cancelText: 'Cancel'
              });
              
              if (!confirmed) {
                return {
                  cancelled: true, 
                  message: "Operation cancelled by user" 
                };
              }
            } catch (error) {
              console.error("Error showing confirmation dialog:", error);
              
              // Fallback to standard confirm if our dialog fails
              const confirmChange = window.confirm(
                `This business already has a chart of accounts set up. Applying a new ${businessTypeString.replace(/_/g, ' ')} industry template will overwrite the existing accounts. Do you want to continue?`
              );
              
              if (!confirmChange) {
                return {
                  cancelled: true, 
                  message: "Operation cancelled by user" 
                };
              }
            }
          }
        }
        
        // Prepare request payload with regenerate flag if needed
        const payload = typeof params === 'object' 
          ? { ...params, regenerate: true } 
          : { businessType: params, regenerate: true };
        
        console.log("Sending payload:", payload);
        
        const res = await apiRequest(
          "POST",
          `/api/businesses/${businessId}/chart-of-accounts`,
          payload
        );
        
        // Parse the response body
        const responseData = await res.text();
        let data: any = { success: true };
        
        try {
          if (responseData) {
            data = JSON.parse(responseData);
          }
        } catch (parseError) {
          console.error("Error parsing chart response:", parseError);
          data = { success: true };
        }
        
        // Handle various response cases
        if (res.status === 200 && data.already_exists) {
          console.log("Chart already exists, redirecting to direct accounts");
          // Force refresh the queries to ensure the latest data
          await queryClient.invalidateQueries({
            queryKey: ["/api/businesses", businessId, "chart-of-accounts"],
          });
          
          // Also invalidate the accounts query to ensure we fetch the latest accounts
          if (chartOfAccounts?.id) {
            await queryClient.invalidateQueries({
              queryKey: ["/api/chart-of-accounts", chartOfAccounts.id, "accounts"],
            });
          }
          
          // Add timestamp to force a clean reload instead of cache
          window.location.href = "/direct-accounts?refresh=" + new Date().getTime();
          return { success: true, message: "Chart of accounts already exists" };
        }
        
        // Handle the new restriction case (403 Forbidden)
        if (res.status === 403 && data.restrictedChange) {
          console.log("Template change restricted: Business already set up");
          
          // Show toast notification and navigate to direct accounts to view existing accounts
          toast({
            title: "Template Change Restricted",
            description: data.message || "Chart of accounts template cannot be changed after initial setup. You can add or edit individual accounts instead.",
            variant: "destructive",
            duration: 6000
          });
          
          // Navigate to direct accounts to view/edit existing accounts
          setTimeout(() => {
            window.location.href = "/direct-accounts?refresh=" + new Date().getTime();
          }, 1000);
          
          return {
            success: false,
            restricted: true,
            message: data.message || "Chart of accounts template cannot be changed after initial setup"
          };
        }
        
        // Also handle 409 Conflict specifically
        if (res.status === 409) {
          console.log("Chart already exists (409), redirecting to direct accounts");
          // Force refresh the queries
          await queryClient.invalidateQueries({
            queryKey: ["/api/businesses", businessId, "chart-of-accounts"],
          });
          
          // Also invalidate the accounts query
          if (chartOfAccounts?.id) {
            await queryClient.invalidateQueries({
              queryKey: ["/api/chart-of-accounts", chartOfAccounts.id, "accounts"],
            });
          }
          
          // Add timestamp to force a clean reload
          setTimeout(() => {
            window.location.href = "/direct-accounts?refresh=" + new Date().getTime();
          }, 500);
          return { success: true, message: "Chart of accounts already exists" };
        }
        
        if (!res.ok) {
          throw new Error(data.message || "Failed to generate chart of accounts");
        }
        
        return data;
      } catch (error) {
        console.error("Error generating chart:", error);
        throw error;
      }
    },
    onSuccess: (data) => {
      // Don't show success message if operation was cancelled
      if (data.cancelled) {
        return;
      }
      
      toast({
        title: "Success",
        description: data.message || "Chart of accounts generated successfully",
      });
      
      // Invalidate relevant queries to get fresh data
      queryClient.invalidateQueries({
        queryKey: ["/api/businesses", businessId, "chart-of-accounts"],
      });
      
      // Invalidate the accounts query to ensure we fetch the latest accounts
      if (chartOfAccounts?.id) {
        queryClient.invalidateQueries({
          queryKey: ["/api/chart-of-accounts", chartOfAccounts.id, "accounts"],
        });
      }
      
      // Add timestamp to force a clean reload
      setTimeout(() => {
        window.location.href = "/direct-accounts?refresh=" + new Date().getTime();
      }, 500);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to generate chart of accounts",
        variant: "destructive",
      });
    },
  });

  // Update account mutation
  const updateAccountMutation = useMutation({
    mutationFn: async (accountData: any) => {
      if (!accountData.id) {
        throw new Error("Account ID is required");
      }
      
      const res = await apiRequest(
        "PATCH",
        `/api/accounts/${accountData.id}`,
        accountData
      );
      return await res.json();
    },
    onSuccess: () => {
      // Invalidate accounts query
      if (chartOfAccounts?.id) {
        queryClient.invalidateQueries({
          queryKey: ["/api/chart-of-accounts", chartOfAccounts.id, "accounts"],
        });
      }
    }
  });

  // Create new account mutation
  const createAccountMutation = useMutation({
    mutationFn: async (accountData: any) => {
      if (!accountData.chartId) {
        throw new Error("Chart ID is required");
      }
      
      const res = await apiRequest(
        "POST",
        "/api/accounts",
        accountData
      );
      return await res.json();
    },
    onSuccess: () => {
      // Invalidate accounts query
      if (chartOfAccounts?.id) {
        queryClient.invalidateQueries({
          queryKey: ["/api/chart-of-accounts", chartOfAccounts.id, "accounts"],
        });
      }
    }
  });

  // Check if what's returned is actually a chart of accounts and not just a business
  const isActualChartOfAccounts = chartOfAccounts && 
                                 chartOfAccounts.businessId !== undefined && 
                                 chartOfAccounts.id !== chartOfAccounts.businessId;
  
  // Group accounts by type
  const accountsByType = 
    accounts?.reduce((grouped: Record<string, any[]>, account: any) => {
      const type = account.type || "other";
      if (!grouped[type]) {
        grouped[type] = [];
      }
      grouped[type].push(account);
      return grouped;
    }, {} as Record<string, any[]>) || {};

  // Sort each group by code
  Object.keys(accountsByType).forEach(type => {
    accountsByType[type].sort((a: any, b: any) => a.code.localeCompare(b.code));
  });
  
  // Add console logs for debugging
  console.log("Chart of Accounts Data:", chartOfAccounts);
  console.log("Is actual chart:", isActualChartOfAccounts);
  console.log("Accounts:", accounts);
  console.log("Accounts By Type:", accountsByType);

  return {
    accountTemplates,
    chartOfAccounts: isActualChartOfAccounts ? chartOfAccounts : null,
    accounts,
    accountsByType,
    isLoading: isLoadingTemplates || isLoadingChart || isLoadingAccounts,
    error: templatesError || chartError || accountsError,
    generateChartMutation,
    updateAccountMutation,
    createAccountMutation,
    refetchChart,
    refetchAccounts,
    isActualChartOfAccounts
  };
}